import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Select,
  useModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import type { UserGoogleDriveCredential } from '@customerio/hydra-ui-api';
import { useAuth } from '@/services/auth';
import { getGoogleSheetsAppId } from '@/services/oauth';
import { NO_OAUTH_RESPONSE, accessTokenHasRequiredGoogleSheetsScope, openGoogleSheetsPopup } from '@/services/oauth';
import { openSpreadsheetPicker, type SpreadsheetPicker } from '@/services/google-picker';
import {
  useCreateGoogleDriveCredential,
  useDeleteGoogleDriveCredential,
} from '@/services/db/google-drive-credential/mutations';
import { getGoogleDriveAccessToken } from '@/services/db/google-drive-credential/queries';
import { listSpreadsheetSheets, type SpreadsheetSheetOption } from '@/services/db/google-sheets/queries';
import type { SheetSource } from './collection-payload';
import googleSheetsLogo from '@/assets/images/integrations/google-sheets.svg';
import { useAnalytics } from '@/services/analytics';

type GoogleSheetsSourceButtonProps = {
  workspaceId: number;
  onSelect: (source: SheetSource) => void;
  isDisabled?: boolean;
  children?: string;
};

/**
 * `action` values from the Google Picker `ResponseObject`, lowercased — the
 * loaded `google.picker.Action` enum uses upper case, older builds lower.
 * See https://developers.google.com/drive/picker/reference#response-object
 */
const PICKER_ACTION = {
  picked: 'picked',
  cancel: 'cancel',
  loaded: 'loaded',
  error: 'error',
} as const;

type PickerAction = (typeof PICKER_ACTION)[keyof typeof PICKER_ACTION];

function toPickerAction(action: string | undefined): PickerAction | null {
  const normalized = action?.toLowerCase();
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return Object.values<string>(PICKER_ACTION).includes(normalized ?? '') ? (normalized as PickerAction) : null;
}

/**
 * `loaded` fires while the picker is still on screen; every other action ends our
 * turn at it. Treating unknown and missing actions as terminal is deliberate: the
 * import modal is unmounted while the picker is up, so an action we failed to
 * recognise must still tear the flow down or the user is left with no picker, no
 * modal, and no way to cancel.
 */
function isTerminalPickerAction(action: string | undefined): boolean {
  return toPickerAction(action) !== PICKER_ACTION.loaded;
}

type PickerDocument = {
  id?: string;
  name?: string;
  url?: string;
};

type PickerResponse = {
  action?: string;
  docs?: PickerDocument[];
};

type Spreadsheet = {
  id: string;
  name: string;
};

function isGoogleAuthorizationError(error: unknown): boolean {
  if (typeof error !== 'object' || error == null || !('status' in error) || typeof error.status !== 'number') {
    return false;
  }

  return [400, 401, 403, 422].includes(error.status);
}

export function GoogleSheetsSourceButton({
  workspaceId,
  onSelect,
  isDisabled = false,
  children = 'Import Google Sheet',
}: GoogleSheetsSourceButtonProps) {
  const importModal = useGoogleSheetsImportModal();
  const analytics = useAnalytics();

  return (
    <Button
      variant="secondary"
      iconSrc={googleSheetsLogo}
      isDisabled={isDisabled}
      onClick={() => {
        void analytics.track('import', 'uploadStarted', { source: 'from_google_sheet' });
        importModal.open({ workspaceId, onSelect });
      }}
    >
      {children}
    </Button>
  );
}

export type GoogleSheetsImportModalData = {
  workspaceId: number;
  onSelect: (source: SheetSource) => void;
};

type GoogleSheetsImportModalBodyProps = {
  credential: UserGoogleDriveCredential | null;
  needsAdditionalScopes: boolean;
  needsSpreadsheetSelection: boolean;
  isDeletingCredential: boolean;
  onSwitchAccount: () => void;
  sheets: SpreadsheetSheetOption[];
  sheetId: string | null;
  onSheetChange: (sheetId: string | null) => void;
  isFetchingSheets: boolean;
};

function GoogleSheetsImportModalBody({
  credential,
  needsAdditionalScopes,
  needsSpreadsheetSelection,
  isDeletingCredential,
  onSwitchAccount,
  sheets,
  sheetId,
  onSheetChange,
  isFetchingSheets,
}: GoogleSheetsImportModalBodyProps) {
  if (credential == null) {
    return <Paragraph>Log in to your Google account to grant Customer.io access to your Google Sheet.</Paragraph>;
  }

  if (needsAdditionalScopes) {
    return (
      <Paragraph>In order to continue, you must grant Customer.io access to Drive files that you select.</Paragraph>
    );
  }

  if (needsSpreadsheetSelection) {
    return (
      <Paragraph>
        Logged in as {credential.account}.{' '}
        <Link isDisabled={isDeletingCredential} onClick={onSwitchAccount}>
          Switch account
        </Link>
      </Paragraph>
    );
  }

  return (
    <Select
      label="Sheet to import"
      options={sheets}
      value={sheetId}
      placeholder="Select a sheet"
      onChange={onSheetChange}
      isLoading={isFetchingSheets}
      isDisabled={isFetchingSheets}
    />
  );
}

type GoogleSheetsImportModalActionsProps = {
  credential: UserGoogleDriveCredential | null;
  needsAdditionalScopes: boolean;
  needsSpreadsheetSelection: boolean;
  isCreatingCredential: boolean;
  isStartingPicker: boolean;
  canImport: boolean;
  onClose: () => void;
  onConnectGoogle: () => void;
  onStartPicker: () => void;
  onResetSheet: () => void;
  onImportSheet: () => void;
};

function GoogleSheetsImportModalActions({
  credential,
  needsAdditionalScopes,
  needsSpreadsheetSelection,
  isCreatingCredential,
  isStartingPicker,
  canImport,
  onClose,
  onConnectGoogle,
  onStartPicker,
  onResetSheet,
  onImportSheet,
}: GoogleSheetsImportModalActionsProps) {
  let action: ReactNode;

  if (credential == null) {
    action = (
      <Button variant="primary" isLoading={isCreatingCredential} onClick={onConnectGoogle}>
        Sign in with Google
      </Button>
    );
  } else if (needsAdditionalScopes) {
    action = (
      <Button variant="primary" isLoading={isCreatingCredential} onClick={onConnectGoogle}>
        Grant Drive access
      </Button>
    );
  } else if (needsSpreadsheetSelection) {
    action = (
      <Button variant="primary" isLoading={isStartingPicker} onClick={onStartPicker}>
        Select Google Sheet
      </Button>
    );
  } else {
    action = (
      <>
        <Button variant="secondary" onClick={onResetSheet}>
          Change spreadsheet
        </Button>
        <Button variant="primary" isDisabled={!canImport} onClick={onImportSheet}>
          Import
        </Button>
      </>
    );
  }

  return (
    <>
      <Button variant="tertiary" onClick={onClose}>
        Cancel
      </Button>
      {action}
    </>
  );
}

function useGoogleCredential(accountId: number, userId: number, initialCredential: UserGoogleDriveCredential | null) {
  const snackbars = useSnackbars();
  const createCredential = useCreateGoogleDriveCredential({ accountId, userId });
  const deleteCredential = useDeleteGoogleDriveCredential({ accountId, userId });
  const [credential, setCredential] = useState<UserGoogleDriveCredential | null>(initialCredential);
  const [hasRequiredScopes, setHasRequiredScopes] = useState(true);
  const [hasValidAuthorization, setHasValidAuthorization] = useState(true);

  useEffect(() => setCredential(initialCredential), [initialCredential]);

  const connectGoogle = async () => {
    const additionalGrant = credential != null && hasValidAuthorization && !hasRequiredScopes;
    try {
      const oauth = await openGoogleSheetsPopup({ additionalGrant });
      const user = await createCredential.mutateAsync({ oauth });
      setCredential(user.google_drive_credential);
      setHasValidAuthorization(true);

      if (additionalGrant) {
        const accessToken = await getGoogleDriveAccessToken({ accountId, userId });
        setHasRequiredScopes(await accessTokenHasRequiredGoogleSheetsScope(accessToken));
      } else {
        setHasRequiredScopes(true);
      }
    } catch (error) {
      if (error instanceof Error && error.message === NO_OAUTH_RESPONSE) return;
      snackbars.create({ message: "Google Sheets couldn't be connected. Try again.", variant: 'error' });
    }
  };

  const switchAccount = async () => {
    try {
      await deleteCredential.mutateAsync();
      setCredential(null);
      setHasRequiredScopes(true);
      setHasValidAuthorization(true);
      return true;
    } catch {
      snackbars.create({ message: "Google account couldn't be switched. Try again.", variant: 'error' });
      return false;
    }
  };

  return {
    credential,
    hasRequiredScopes,
    hasValidAuthorization,
    setHasRequiredScopes,
    setHasValidAuthorization,
    connectGoogle,
    switchAccount,
    isCreatingCredential: createCredential.isPending,
    isDeletingCredential: deleteCredential.isPending,
  };
}

function useSpreadsheetSelection(workspaceId: number) {
  const snackbars = useSnackbars();
  const requestId = useRef(0);
  const [spreadsheet, setSpreadsheet] = useState<Spreadsheet | null>(null);
  const [sheets, setSheets] = useState<SpreadsheetSheetOption[]>([]);
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [isFetchingSheets, setIsFetchingSheets] = useState(false);
  const selectedSheet = useMemo(() => sheets.find((sheet) => sheet.value === sheetId) ?? null, [sheetId, sheets]);

  const resetSheet = () => {
    requestId.current += 1;
    setSpreadsheet(null);
    setSheets([]);
    setSheetId(null);
    setIsFetchingSheets(false);
  };

  const fetchSheets = async (selectedSpreadsheet: Spreadsheet) => {
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    setSpreadsheet(selectedSpreadsheet);
    setSheets([]);
    setSheetId(null);
    setIsFetchingSheets(true);
    try {
      const sheetOptions = await listSpreadsheetSheets({ workspaceId, spreadsheetId: selectedSpreadsheet.id });
      if (requestId.current !== currentRequestId) return;

      if (sheetOptions.length === 0) {
        resetSheet();
        snackbars.create({ message: "The spreadsheet doesn't contain any sheets to import.", variant: 'error' });
        return;
      }
      setSheets(sheetOptions);
      setSheetId(sheetOptions[0]?.value ?? null);
    } catch {
      if (requestId.current !== currentRequestId) return;

      resetSheet();
      snackbars.create({
        message: "The spreadsheet couldn't be accessed. Check your access to the sheet and try again.",
        variant: 'error',
      });
    } finally {
      if (requestId.current === currentRequestId) setIsFetchingSheets(false);
    }
  };

  return { spreadsheet, sheets, sheetId, setSheetId, selectedSheet, isFetchingSheets, resetSheet, fetchSheets };
}

type UseSpreadsheetPickerProps = {
  accountId: number;
  userId: number;
  onAuthorizationRejected: () => void;
  onScopeChecked: (hasRequiredScopes: boolean) => void;
  onSpreadsheetPicked: (spreadsheet: Spreadsheet) => Promise<void>;
};

function useSpreadsheetPicker({
  accountId,
  userId,
  onAuthorizationRejected,
  onScopeChecked,
  onSpreadsheetPicked,
}: UseSpreadsheetPickerProps) {
  const snackbars = useSnackbars();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isStartingPicker, setIsStartingPicker] = useState(false);
  const pickerRef = useRef<SpreadsheetPicker | null>(null);
  // Bumped whenever the flow is torn down, so a picker that only finishes opening
  // afterwards gets dismissed instead of stored.
  const pickerId = useRef(0);

  // Tears down without touching state, so it is also safe as unmount cleanup.
  // Hiding is idempotent: the picker already hid itself on `picked`/`cancel`, but
  // after an error or unrecognised action it may still be up, and this handle is
  // the only thing that can take it down.
  const abandonPicker = () => {
    pickerId.current += 1;
    pickerRef.current?.setVisible(false);
    pickerRef.current = null;
  };

  const closePicker = () => {
    abandonPicker();
    setIsPickerOpen(false);
  };

  // The picker lives outside React's tree, so unmounting the modal (parent closed,
  // route change) would otherwise strand its overlay on screen.
  useEffect(() => abandonPicker, []);

  const handlePickerResponse = async (response: PickerResponse) => {
    const action = toPickerAction(response.action);
    if (!isTerminalPickerAction(response.action)) return;

    // Clear before anything that can bail out, so no path leaves the modal
    // unmounted with the picker gone.
    closePicker();

    if (action !== PICKER_ACTION.picked) {
      if (action === PICKER_ACTION.error) {
        snackbars.create({ message: "Google Sheet couldn't be opened. Try again.", variant: 'error' });
      }
      return;
    }

    const [selectedSpreadsheet] = response.docs ?? [];
    if (selectedSpreadsheet?.id == null || selectedSpreadsheet.name == null) return;

    await onSpreadsheetPicked({ id: selectedSpreadsheet.id, name: selectedSpreadsheet.name });
  };

  const startPicker = async () => {
    setIsStartingPicker(true);
    let accessToken: string;

    try {
      accessToken = await getGoogleDriveAccessToken({ accountId, userId });
    } catch (error) {
      if (isGoogleAuthorizationError(error)) onAuthorizationRejected();
      snackbars.create({ message: "Google Sheet couldn't be opened. Try again.", variant: 'error' });
      setIsStartingPicker(false);
      return;
    }

    try {
      const hasScope = await accessTokenHasRequiredGoogleSheetsScope(accessToken);
      onScopeChecked(hasScope);
      if (!hasScope) return;

      const currentPickerId = pickerId.current + 1;
      pickerId.current = currentPickerId;
      setIsPickerOpen(true);

      const picker = await openSpreadsheetPicker({
        accessToken,
        appId: getGoogleSheetsAppId(),
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        callback: (response) => void handlePickerResponse(response as PickerResponse),
      });

      // The callback can settle the flow — or the modal can unmount — before this
      // resolves. Storing the handle then would orphan a visible picker.
      if (pickerId.current !== currentPickerId) {
        picker.setVisible(false);
        return;
      }
      pickerRef.current = picker;
    } catch {
      closePicker();
      snackbars.create({ message: "Google Sheet couldn't be opened. Try again.", variant: 'error' });
    } finally {
      setIsStartingPicker(false);
    }
  };

  return { isPickerOpen, isStartingPicker, startPicker };
}

export function GoogleSheetsImportModal({
  modalState,
  data,
  onClose,
}: PlumaManagedModalProps<GoogleSheetsImportModalData>) {
  const { workspaceId, onSelect } = data;
  const { account, user } = useAuth();
  const credential = useGoogleCredential(account.id, user.id, user.google_drive_credential ?? null);
  const selection = useSpreadsheetSelection(workspaceId);
  const picker = useSpreadsheetPicker({
    accountId: account.id,
    userId: user.id,
    onAuthorizationRejected: () => credential.setHasValidAuthorization(false),
    onScopeChecked: credential.setHasRequiredScopes,
    onSpreadsheetPicked: selection.fetchSheets,
  });
  const needsAdditionalScopes =
    credential.credential != null && (!credential.hasValidAuthorization || !credential.hasRequiredScopes);
  const needsSpreadsheetSelection =
    credential.credential != null && credential.hasRequiredScopes && selection.spreadsheet == null;

  const switchAccount = async () => {
    if (await credential.switchAccount()) selection.resetSheet();
  };

  const importSheet = () => {
    if (selection.spreadsheet == null || selection.selectedSheet == null) {
      return;
    }

    onSelect({
      sheet_id: Number(selection.selectedSheet.value),
      sheet_name: selection.selectedSheet.label,
      spreadsheet_id: selection.spreadsheet.id,
      spreadsheet_name: selection.spreadsheet.name,
    });
    onClose();
  };

  // The Picker renders its own full-screen overlay, so unmount ours while it is
  // up rather than stacking two dialogs.
  if (picker.isPickerOpen) {
    return null;
  }

  return (
    <Modal state={modalState} onClose={onClose} title="Import Google Sheet">
      <ModalBody>
        <GoogleSheetsImportModalBody
          credential={credential.credential}
          needsAdditionalScopes={needsAdditionalScopes}
          needsSpreadsheetSelection={needsSpreadsheetSelection}
          isDeletingCredential={credential.isDeletingCredential}
          onSwitchAccount={() => void switchAccount()}
          sheets={selection.sheets}
          sheetId={selection.sheetId}
          onSheetChange={selection.setSheetId}
          isFetchingSheets={selection.isFetchingSheets}
        />
      </ModalBody>
      <ModalFooter>
        <GoogleSheetsImportModalActions
          credential={credential.credential}
          needsAdditionalScopes={needsAdditionalScopes}
          needsSpreadsheetSelection={needsSpreadsheetSelection}
          isCreatingCredential={credential.isCreatingCredential}
          isStartingPicker={picker.isStartingPicker}
          canImport={selection.selectedSheet != null}
          onClose={onClose}
          onConnectGoogle={() => void credential.connectGoogle()}
          onStartPicker={() => void picker.startPicker()}
          onResetSheet={selection.resetSheet}
          onImportSheet={importSheet}
        />
      </ModalFooter>
    </Modal>
  );
}

export function useGoogleSheetsImportModal() {
  return useModal(GoogleSheetsImportModal);
}
